/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  manualSearch thunk
 */

import type { AutocompleteApiResult, AutocompleteCurrentPositionResult } from '@zcode/bizos'
import { trackGAEvent } from '@zcode/nr-utils'
import { isNumber } from '@zcode/utils'
import type { Dispatch, SetStateAction } from 'react'
import type * as Api from '../../../../../shared/api'
import {
  addResultToPreviousSearches,
  getManualSearchResult,
} from '../../../../../shared/components/Autocomplete/helpers'
import getCurrentPosition from '../../../../../shared/components/Autocomplete/module/thunks/getCurrentPosition'
import { AUTOCOMPLETE_TYPE, AUTOCOMPLETE_USE } from '../../../../../shared/constants/analytics'
import type { Dispatch as NrDispatch } from '../../../../../shared/types'
import type { AutocompleteOrigin } from '../../constants'
import { getPreviousSearches } from '../../helpers/getPreviousSearches'

interface ManualSearchProps {
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
  onError?: (error: { message: string }) => void
  onSelectAutocompleteResult: (
    result: AutocompleteApiResult | AutocompleteCurrentPositionResult
  ) => void
  origin?: AutocompleteOrigin
  hoveredIndex: number
  predictionResults: readonly AutocompleteApiResult[] | null
  searchTerm: string
  setPredictionResults: Dispatch<SetStateAction<readonly AutocompleteApiResult[] | null>>
  setSearchTerm: Dispatch<SetStateAction<string>>
}

const manualSearch =
  ({
    exclude,
    onSelectAutocompleteResult,
    onError,
    origin,
    hoveredIndex,
    predictionResults,
    searchTerm,
    setPredictionResults,
    setSearchTerm,
  }: ManualSearchProps) =>
  (dispatch: NrDispatch) => {
    const prevSearches = getPreviousSearches(exclude)

    if (searchTerm || (isNumber(hoveredIndex) && hoveredIndex >= 0)) {
      const result = getManualSearchResult({
        hoveredIndex,
        predictionResults,
        prevSearches,
        searchTerm,
      })
      // this dispatches to NZR/NPR redux state
      if (result) {
        onSelectAutocompleteResult(result)
        addResultToPreviousSearches(result)
        trackGAEvent(`${AUTOCOMPLETE_USE} - ${origin}`, 'enter')
        trackGAEvent(`${AUTOCOMPLETE_TYPE} - ${origin}`, 'location', result.type)
      } else {
        dispatch(getCurrentPosition({ onSelectAutocompleteResult, onError, origin }))
      }

      setPredictionResults(null)
      setSearchTerm('')
    }
  }

export default manualSearch
